import React from "react";

import {
    Spinner,
    Button,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    DialogTrigger,
    Toaster,
    Toast,
    ToastTitle,
    ToastTrigger,
    ToastBody,
    ToastFooter,
    ToastIntent,
    useToastController,
    makeStyles,
    useId,
    tokens,
} from "@fluentui/react-components";
import { Dismiss12Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
    toast: {
        paddingBottom: "15px",
    },
    closeButton: {
        padding: 0,
    },
    spinner: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        position: "fixed",
        backgroundColor: tokens.colorNeutralShadowAmbient,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 100,
    }
});

interface UtilitiesContextProps {
    showNotification: (toast: ToastProps) => void;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    showDialog: (dialog: DialogProps) => void;
}

interface ToastProps {
    type: ToastIntent;
    title: string | React.ReactNode;
    message: string | React.ReactNode;
    footer?: string | React.ReactNode;
}

interface DialogProps {
    title: React.ReactNode;
    content: React.ReactNode;
    primaryActionText: string;
    primaryAction: () => void;
    secondaryActionText: string;
    secondaryAction: () => void;
}

const UtilitiesContext = React.createContext<UtilitiesContextProps>(null!);

export function UtilitiesProvider({ children }: { children: React.ReactNode }) {
    const styles = useStyles();

    const toasterId = useId("global-toaster");
    const { dispatchToast } = useToastController(toasterId);

    const [isLoading, setIsLoading] = React.useState(false);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [dialogProps, setDialogProps] = React.useState<DialogProps | undefined>();

    const showNotification = React.useCallback(({ type, title, message, footer }: ToastProps) => {
        setTimeout(() => {
            dispatchToast((
                <Toast className={styles.toast}>

                    <ToastTitle
                        action={
                            <ToastTrigger>
                                <Button className={styles.closeButton} icon={<Dismiss12Regular />} shape='circular' appearance='subtle' />
                            </ToastTrigger>
                        }>
                        {title}
                    </ToastTitle>

                    <ToastBody>
                        {message}
                    </ToastBody>

                    {
                        footer && <ToastFooter>{footer}</ToastFooter>
                    }

                </Toast>
            ), { intent: type, timeout: 7000, pauseOnHover: true })
        }, 200);
    }, [dispatchToast, styles.toast, styles.closeButton]);

    const showDialog = React.useCallback((dialog: DialogProps) => {
        setDialogProps(dialog);
        setIsDialogOpen(true);
    }, []);

    const dialog = React.useMemo(() => {
        const { title, content, primaryActionText, primaryAction, secondaryActionText, secondaryAction } = dialogProps ?? {};
        return (
            <Dialog open={isDialogOpen} onOpenChange={(e, d) => setIsDialogOpen(d.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogContent>
                            {content}
                        </DialogContent>
                        <DialogActions>

                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" onClick={secondaryAction}>
                                    {secondaryActionText}
                                </Button>
                            </DialogTrigger>

                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="primary" onClick={primaryAction}>
                                    {primaryActionText}
                                </Button>
                            </DialogTrigger>

                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        )
    }, [isDialogOpen, setIsDialogOpen, dialogProps]);

    return (
        <UtilitiesContext.Provider value={{ showNotification, setIsLoading, showDialog }}>
            {isLoading && <Spinner className={styles.spinner} aria-label="loading..." />}
            {dialog}
            {children}
            <Toaster toasterId={toasterId} />
        </UtilitiesContext.Provider>
    );
}

export function useUtilitiesContext() {
    return React.useContext(UtilitiesContext);
}